// Custom item / spell tooltip — replaces the native `title` attribute UX
// which is (a) ugly and (b) shows up with a ~1s delay. Singleton element
// mounted to body, shared by every panel. Each consumer calls
// `bindItemTooltip` or `bindSpellTooltip` and the tooltip handles
// pointer enter / leave / position itself.
//
// Positioning: anchored to the right of the source element with an offset.
// Clamps to the viewport so chips near the right edge slide back inside.

import {
  type Affix,
  formatAffix,
  type ItemInstance,
  isTwoHanded,
  type ItemTemplate,
  type SpellTemplate,
} from "@vinyl/shared";
import { el } from "./dom.js";

let singleton: HTMLElement | null = null;
let activeAnchor: HTMLElement | null = null;

function ensureSingleton(): HTMLElement {
  if (singleton) return singleton;
  const t = el("div", { class: "item-tooltip hidden" });
  document.body.append(t);
  singleton = t;
  return t;
}

export interface BindOptions {
  // Optional override of the resolved template name — useful if the
  // caller wants to prefix something. Defaults to `template.name`.
  nameOverride?: string;
}

export function bindItemTooltip(
  element: HTMLElement,
  template: ItemTemplate,
  instance?: ItemInstance,
  opts?: BindOptions,
): void {
  const tip = ensureSingleton();
  element.addEventListener("pointerenter", () => {
    renderTooltip(tip, template, instance, opts?.nameOverride);
    positionTooltip(tip, element);
    activeAnchor = element;
  });
  element.addEventListener("pointerleave", () => {
    if (activeAnchor === element) {
      tip.classList.add("hidden");
      activeAnchor = null;
    }
  });
  // Also clear on click — the slot's click handler often re-renders the
  // panel, which destroys the original anchor without firing leave.
  element.addEventListener("click", () => {
    tip.classList.add("hidden");
    activeAnchor = null;
  });
}

// Hide the tooltip explicitly. Call when a panel that owns a hovered slot
// closes / re-renders so a stale tooltip doesn't linger.
export function hideItemTooltip(): void {
  if (singleton) singleton.classList.add("hidden");
  activeAnchor = null;
}

// =============================================================================
// Spell tooltip — same singleton, different renderer.
// =============================================================================

export interface SpellTooltipOptions {
  // Whether the local player meets the requirements; drives a "locked" hint
  // at the bottom of the tooltip.
  known: boolean;
  // Optional footer hint (e.g. "Right-click to clear" on hotbar slots).
  footerHint?: string;
}

export function bindSpellTooltip(
  element: HTMLElement,
  spell: SpellTemplate,
  opts: SpellTooltipOptions,
): void {
  const tip = ensureSingleton();
  element.addEventListener("pointerenter", () => {
    renderSpellTooltip(tip, spell, opts);
    positionTooltip(tip, element);
    activeAnchor = element;
  });
  element.addEventListener("pointerleave", () => {
    if (activeAnchor === element) {
      tip.classList.add("hidden");
      activeAnchor = null;
    }
  });
  element.addEventListener("click", () => {
    tip.classList.add("hidden");
    activeAnchor = null;
  });
}

function renderSpellTooltip(
  tip: HTMLElement,
  spell: SpellTemplate,
  opts: SpellTooltipOptions,
): void {
  tip.replaceChildren();
  tip.classList.remove("hidden");
  tip.className =
    "item-tooltip tooltip-spell tooltip-spell-" +
    spell.school +
    (opts.known ? "" : " tooltip-locked");

  // Header — name + school chip.
  const header = el("div", { class: "tooltip-header" });
  header.append(el("span", { class: "tooltip-name" }, spell.name));
  header.append(
    el(
      "span",
      { class: `tooltip-tag tag-school tag-school-${spell.school}` },
      spell.school,
    ),
  );
  tip.append(header);

  // Cast pipeline section.
  const pipeline = el("div", { class: "tooltip-section" });
  pipeline.append(
    statRow("Mana cost", String(spell.manaCost)),
    statRow(
      "Cast time",
      spell.castTimeMs === 0 ? "Instant" : `${(spell.castTimeMs / 1000).toFixed(1)}s`,
    ),
    statRow("Cooldown", `${(spell.cooldownMs / 1000).toFixed(1)}s`),
  );
  if (spell.range > 0) pipeline.append(statRow("Range", `${spell.range} tiles`));
  if (spell.targeting === "self") {
    pipeline.append(el("div", { class: "tooltip-hint" }, "Self-cast"));
  } else if (spell.targeting === "aoeGround" && spell.aoeRadius) {
    pipeline.append(statRow("AOE radius", `${spell.aoeRadius} tiles`));
  }
  tip.append(pipeline);

  // Effects.
  if (spell.effects.length > 0) {
    const effects = el("div", { class: "tooltip-section tooltip-affixes" });
    for (const effect of spell.effects) {
      effects.append(
        el(
          "div",
          { class: "tooltip-affix tooltip-stat-positive" },
          formatEffect(effect),
        ),
      );
    }
    tip.append(effects);
  }

  // Requirements + lock state + custom footer hint.
  const footer = el("div", { class: "tooltip-section tooltip-footer" });
  const reqs: string[] = [];
  if (spell.requirement.int !== undefined) reqs.push(`INT ${spell.requirement.int}`);
  if (spell.requirement.fai !== undefined) reqs.push(`FAI ${spell.requirement.fai}`);
  if (reqs.length > 0) {
    footer.append(
      el(
        "div",
        { class: "tooltip-stat" },
        el("span", { class: "tooltip-stat-key" }, "Requires"),
        el(
          "span",
          {
            class: opts.known
              ? "tooltip-stat-val tooltip-reqs"
              : "tooltip-stat-val tooltip-stat-negative",
          },
          reqs.join(", "),
        ),
      ),
    );
  }
  if (!opts.known) {
    footer.append(el("div", { class: "tooltip-hint" }, "Locked: requirements not met."));
  }
  if (opts.footerHint) {
    footer.append(el("div", { class: "tooltip-hint" }, opts.footerHint));
  }
  if (footer.children.length > 0) tip.append(footer);
}

function statRow(key: string, val: string): HTMLElement {
  return el(
    "div",
    { class: "tooltip-stat" },
    el("span", { class: "tooltip-stat-key" }, key),
    el("span", { class: "tooltip-stat-val" }, val),
  );
}

function formatEffect(effect: SpellTemplate["effects"][number]): string {
  switch (effect.kind) {
    case "damage":
      return `Damage ${effect.basePower}`;
    case "heal":
      return `Heal ${effect.basePower}`;
    case "dot":
      return `DoT ${effect.tickPower} / ${(effect.tickIntervalMs / 1000).toFixed(1)}s × ${Math.ceil(effect.durationMs / effect.tickIntervalMs)}`;
    case "slow":
      return `Slow ${Math.round((1 - effect.speedMult) * 100)}% for ${(effect.durationMs / 1000).toFixed(1)}s`;
    case "absorb":
      return `Absorb ${effect.basePool} (+${effect.intScale}×INT) for ${(effect.durationMs / 1000).toFixed(1)}s`;
    case "buffDmg":
      return `Buff +${Math.round(effect.dmgPct * 100)}% dmg for ${(effect.durationMs / 1000).toFixed(1)}s`;
  }
}

function renderTooltip(
  tip: HTMLElement,
  template: ItemTemplate,
  instance: ItemInstance | undefined,
  nameOverride: string | undefined,
): void {
  tip.replaceChildren();
  tip.classList.remove("hidden");

  // Rarity class drives the name color + border accent.
  const isUnique = template.rarity === "unique";
  const isRare = !!instance && instance.affixes.length > 0;
  tip.className =
    "item-tooltip" +
    (isUnique ? " tooltip-unique" : isRare ? " tooltip-rare" : "");

  // Header: name + rarity tag.
  const header = el("div", { class: "tooltip-header" });
  header.append(
    el("span", { class: "tooltip-name" }, nameOverride ?? template.name),
  );
  if (isUnique) {
    header.append(el("span", { class: "tooltip-tag tag-unique" }, "Unique"));
  } else if (isRare) {
    header.append(el("span", { class: "tooltip-tag tag-rare" }, "Rare"));
  }
  tip.append(header);

  // Base stats block — weapon dmg, defense, consumable effect.
  const baseLines: HTMLElement[] = [];
  if (template.stats.damage) {
    baseLines.push(
      el(
        "div",
        { class: "tooltip-stat" },
        el("span", { class: "tooltip-stat-key" }, "Damage"),
        el(
          "span",
          { class: "tooltip-stat-val" },
          `${template.stats.damage.min}–${template.stats.damage.max}`,
        ),
      ),
    );
  }
  if (template.stats.weaponType) {
    baseLines.push(
      el(
        "div",
        { class: "tooltip-stat" },
        el("span", { class: "tooltip-stat-key" }, "Weapon type"),
        el("span", { class: "tooltip-stat-val" }, template.stats.weaponType),
      ),
    );
  }
  if (typeof template.stats.defense === "number" && template.stats.defense > 0) {
    baseLines.push(
      el(
        "div",
        { class: "tooltip-stat" },
        el("span", { class: "tooltip-stat-key" }, "Defense"),
        el("span", { class: "tooltip-stat-val" }, String(template.stats.defense)),
      ),
    );
  }
  if (template.stats.consumable) {
    const c = template.stats.consumable;
    baseLines.push(
      el(
        "div",
        { class: "tooltip-stat" },
        el("span", { class: "tooltip-stat-key" }, humanizeKind(c.kind)),
        el("span", { class: "tooltip-stat-val tooltip-stat-positive" }, `+${c.amount}`),
      ),
    );
  }
  if (isTwoHanded(template)) {
    baseLines.push(el("div", { class: "tooltip-hint" }, "Two-handed"));
  }
  if (baseLines.length > 0) {
    tip.append(el("div", { class: "tooltip-section" }, ...baseLines));
  }

  // Affixes — template-fixed first (unique items), then instance-rolled
  // (rare drops). Color-coded green for positive bonuses.
  const allAffixes: Affix[] = [];
  if (template.affixes) allAffixes.push(...template.affixes);
  if (instance) allAffixes.push(...instance.affixes);
  if (allAffixes.length > 0) {
    const section = el("div", { class: "tooltip-section tooltip-affixes" });
    for (const a of allAffixes) {
      section.append(
        el(
          "div",
          { class: "tooltip-affix" + (a.value >= 0 ? " tooltip-stat-positive" : " tooltip-stat-negative") },
          formatAffix(a),
        ),
      );
    }
    tip.append(section);
  }

  // Footer: weight + requirements.
  const footer = el("div", { class: "tooltip-section tooltip-footer" });
  footer.append(
    el(
      "div",
      { class: "tooltip-stat" },
      el("span", { class: "tooltip-stat-key" }, "Weight"),
      el("span", { class: "tooltip-stat-val" }, `${template.weight} kg`),
    ),
  );
  const reqs: string[] = [];
  for (const k of ["str", "dex", "con", "int", "fai", "rap"] as const) {
    const v = template.requirements[k];
    if (v !== undefined) reqs.push(`${k.toUpperCase()} ${v}`);
  }
  if (reqs.length > 0) {
    footer.append(
      el(
        "div",
        { class: "tooltip-stat" },
        el("span", { class: "tooltip-stat-key" }, "Requires"),
        el("span", { class: "tooltip-stat-val tooltip-reqs" }, reqs.join(", ")),
      ),
    );
  }
  tip.append(footer);
}

function positionTooltip(tip: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  // Force a synchronous layout pass before reading the tooltip's own size.
  tip.style.left = "0px";
  tip.style.top = "0px";
  const tipRect = tip.getBoundingClientRect();
  const margin = 8;

  // Default: anchor to the right of the source.
  let left = rect.right + margin;
  let top = rect.top;

  // If we'd overflow the right edge, anchor to the left instead.
  if (left + tipRect.width > window.innerWidth - margin) {
    left = rect.left - tipRect.width - margin;
  }
  // Clamp vertically to the viewport.
  if (top + tipRect.height > window.innerHeight - margin) {
    top = Math.max(margin, window.innerHeight - tipRect.height - margin);
  }
  if (top < margin) top = margin;
  if (left < margin) left = margin;

  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

function humanizeKind(kind: string): string {
  switch (kind) {
    case "heal":
      return "Heal";
    case "restoreManaArc":
    case "restoreManaDiv":
      return "Restore mana";
    case "food":
      return "Food";
    default:
      return kind;
  }
}
